type OnStart = (
  status: number,
  statusText: string,
  contentType: string | null,
  headers: { get(name: string): string | null },
) => void;
type OnProgress = (chunk: string) => void;
type OnFinish = (error: unknown) => void;

/**
 * Fetch transport for `EventSourcePolyfill` (passed through its `Transport` option).
 *
 * This mirrors the polyfill's built-in `FetchTransport` (event-source-polyfill 1.0.31,
 * `src/eventsource.js`) with one difference: `abort()` handles the promise returned by
 * `reader.cancel()`. Cancelling a reader whose stream has already errored rejects with the
 * stream's stored error (Streams spec, ReadableStreamCancel), and the built-in transport leaves
 * that promise unhandled. Since `onerror` on the conversation page calls `close()` right after
 * the stream drops, every disconnect surfaced as an unhandled rejection — "TypeError: network
 * error" on Chromium, "TypeError: Error in input stream" on Firefox (BABYSITS-FRONTEND-14P,
 * -14S; upstream Yaffle/EventSource#195, no release since 2022).
 *
 * The first `open()` argument is the polyfill's XHR wrapper; the fetch transport never uses it.
 */
export class MercureFetchTransport {
  open(
    _xhr: unknown,
    onStart: OnStart,
    onProgress: OnProgress,
    onFinish: OnFinish,
    url: string,
    withCredentials: boolean,
    headers: Record<string, string>,
  ): { abort: () => void } {
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    const controller = new AbortController();
    const textDecoder = new TextDecoder();

    fetch(url, {
      headers,
      credentials: withCredentials ? "include" : "same-origin",
      signal: controller.signal,
      cache: "no-store",
    })
      .then((response) => {
        if (!response.body) {
          throw new TypeError("Response has no body");
        }

        const bodyReader = response.body.getReader();
        reader = bodyReader;

        onStart(
          response.status,
          response.statusText,
          response.headers.get("Content-Type"),
          response.headers,
        );

        return new Promise<void>((resolve, reject) => {
          const readNextChunk = () => {
            bodyReader
              .read()
              .then((result) => {
                if (result.done) {
                  // Note: bytes left in textDecoder are ignored, as in the polyfill.
                  resolve();
                  return;
                }

                onProgress(textDecoder.decode(result.value, { stream: true }));
                readNextChunk();
              })
              .catch(reject);
          };

          readNextChunk();
        });
      })
      .then(
        () => undefined,
        (error: unknown) =>
          error instanceof Error && error.name === "AbortError" ? undefined : error,
      )
      .then((error) => {
        onFinish(error);
      });

    return {
      abort: () => {
        // The one line that differs from the polyfill: swallow the rejection of cancelling an
        // already-errored stream. The error was already reported through onFinish/onerror.
        reader?.cancel().catch(() => {});
        controller.abort();
      },
    };
  }
}
